import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import SectionActions from './SectionActions';
import PrintHeader from './PrintHeader';
import {
  CalendarDays, CheckCircle2, Clock, AlertCircle,
  Users, Plus, Loader2, Trash2
} from 'lucide-react';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const Pianificazione = () => {
  const [attivita, setAttivita] = useState([]);
  const [operai, setOperai] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newAtt, setNewAtt] = useState({ title: '', startDate: '', endDate: '', cantiereId: '', assignedTo: [] });
  const pageRef = useRef(null);

  const loadData = async () => {
    try {
      const [a, o, c] = await Promise.all([api.getAttivita(), api.getOperai(), api.getCantieri()]);
      setAttivita(a); setOperai(o); setCantieri(c);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const filtered = filterStatus ? attivita.filter(a => a.status === filterStatus) : attivita;
  const statusLabel = (s) => ({ completata: 'Completata', in_corso: 'In Corso', da_iniziare: 'Da Iniziare' }[s] || s);
  const statusColor = (s) => ({ completata: '#22c55e', in_corso: '#3b82f6', da_iniziare: '#9ca3af' }[s] || '#6b7280');
  const getCantiereName = (cId) => cantieri.find(c => c.id === cId)?.name || '';

  const handleAdd = async () => {
    if (!newAtt.title || !newAtt.startDate || !newAtt.endDate || !newAtt.cantiereId) return;
    try {
      await api.createAttivita(newAtt);
      setNewAtt({ title: '', startDate: '', endDate: '', cantiereId: '', assignedTo: [] });
      setShowAdd(false);
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    try { await api.deleteAttivita(id); loadData(); } catch (e) { console.error(e); }
  };

  const handleUpdateProgress = async (id, progress) => {
    const status = progress >= 100 ? 'completata' : progress > 0 ? 'in_corso' : 'da_iniziare';
    try { await api.updateAttivita(id, { progress, status }); loadData(); } catch (e) { console.error(e); }
  };

  const completate = attivita.filter(a => a.status === 'completata').length;
  const inCorso = attivita.filter(a => a.status === 'in_corso').length;
  const daIniziare = attivita.filter(a => a.status === 'da_iniziare').length;

  if (loading) return <div className="page-container"><div className="loading-center"><Loader2 size={32} className="spin" /></div></div>;

  return (
    <div className="page-container" ref={pageRef}>
      <PrintHeader title="Pianificazione Lavori" subtitle="Cronoprogramma e gestione attività" />
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">Pianificazione Lavori</h1>
          <p className="page-subtitle">Cronoprogramma e gestione attività</p>
        </div>
        <div className="header-right">
          <SectionActions sectionTitle="Pianificazione Lavori" contentRef={pageRef} />
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <button className="header-action-btn"><Plus size={16} /><span>Nuova Attività</span></button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nuova Attività</DialogTitle></DialogHeader>
              <div className="form-fields">
                <div className="form-field"><Label>Titolo</Label><Input value={newAtt.title} onChange={e => setNewAtt({...newAtt, title: e.target.value})} placeholder="Es: Getto fondazioni" /></div>
                <div className="form-row">
                  <div className="form-field"><Label>Data Inizio</Label><Input type="date" value={newAtt.startDate} onChange={e => setNewAtt({...newAtt, startDate: e.target.value})} /></div>
                  <div className="form-field"><Label>Data Fine</Label><Input type="date" value={newAtt.endDate} onChange={e => setNewAtt({...newAtt, endDate: e.target.value})} /></div>
                </div>
                <div className="form-field"><Label>Cantiere</Label>
                  <Select value={newAtt.cantiereId} onValueChange={v => setNewAtt({...newAtt, cantiereId: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleziona cantiere" /></SelectTrigger>
                    <SelectContent>{cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <button className="form-submit" onClick={handleAdd}>Crea Attività</button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="plan-stats">
        <div className="plan-stat" onClick={() => setFilterStatus(filterStatus === 'completata' ? null : 'completata')}>
          <CheckCircle2 size={20} color="#22c55e" /><span className="plan-stat-val">{completate}</span><span className="plan-stat-lbl">Completate</span>
        </div>
        <div className="plan-stat" onClick={() => setFilterStatus(filterStatus === 'in_corso' ? null : 'in_corso')}>
          <Clock size={20} color="#3b82f6" /><span className="plan-stat-val">{inCorso}</span><span className="plan-stat-lbl">In Corso</span>
        </div>
        <div className="plan-stat" onClick={() => setFilterStatus(filterStatus === 'da_iniziare' ? null : 'da_iniziare')}>
          <AlertCircle size={20} color="#9ca3af" /><span className="plan-stat-val">{daIniziare}</span><span className="plan-stat-lbl">Da Iniziare</span>
        </div>
      </div>

      <div className="activities-list">
        {filtered.map((act) => {
          const assigned = act.assignedTo?.map(id => operai.find(o => o.id === id)).filter(Boolean) || [];
          return (
            <div key={act.id} className="activity-card">
              <div className="act-top">
                <div className="act-status-dot" style={{ background: statusColor(act.status) }} />
                <h4 className="act-title">{act.title}</h4>
                <span className="act-status-label" style={{ color: statusColor(act.status) }}>{statusLabel(act.status)}</span>
                <button className="delete-icon-btn" onClick={() => handleDelete(act.id)}><Trash2 size={15} /></button>
              </div>
              <div className="act-dates">
                <CalendarDays size={14} />
                <span>{new Date(act.startDate).toLocaleDateString('it-IT')} — {new Date(act.endDate).toLocaleDateString('it-IT')}</span>
                <span className="act-cantiere">{getCantiereName(act.cantiereId)}</span>
              </div>
              <div className="act-progress">
                <Progress value={act.progress} className="act-bar" />
                <span className="act-percent">{act.progress}%</span>
                <div className="progress-btns">
                  <button className="progress-btn" onClick={() => handleUpdateProgress(act.id, Math.max(0, act.progress - 10))}>-</button>
                  <button className="progress-btn" onClick={() => handleUpdateProgress(act.id, Math.min(100, act.progress + 10))}>+</button>
                </div>
              </div>
              {assigned.length > 0 && (
                <div className="act-assigned"><Users size={14} />
                  <div className="act-avatars">{assigned.map((w) => <div key={w.id} className="act-avatar" title={w.name}>{w.name.split(' ').map(n => n[0]).join('')}</div>)}</div>
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <p className="empty-text">Nessuna attività. Creane una nuova!</p>}
      </div>
    </div>
  );
};

export default Pianificazione;
